<template>
  <Modal 
    @click.prevent.stop 
    rounded="42px" 
    :isOpen="isOpen" 
    :changeModalState="closeModal"
  >
    <div class="flex flex-col h-[70vh] max-h-[600px] max-w-lg w-full mx-auto relative overflow-hidden">
      <!-- Warning list - scrollable area -->
      <div class="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-16 sm:pb-20 md:pb-24 scrollbar-hide min-h-[200px]">
        <div v-if="warningList.length > 0" class="bg-red-50 p-4 sm:p-6 md:p-8 rounded-2xl border border-red-200 shadow-sm">
          <h3 class="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-6 text-red-700 flex items-center gap-2 md:gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {{ $t('general.security_warnings') }}
          </h3>
          <ul v-if="warningList !== null" class="space-y-2 sm:space-y-3 md:space-y-4">
            <li v-for="(warning, index) in warningList" 
                :key="index" 
                class="text-red-600 flex items-start gap-2 md:gap-3">
              <span class="inline-block w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-red-600 mt-1.5"></span>
              <span class="flex-1 text-sm sm:text-base md:text-lg">{{ warning }}</span>
            </li>
          </ul>
          <p v-else class="text-center text-sm sm:text-base md:text-lg text-red-600">{{ $t('general.no_warnings') }}</p>
        </div>
      </div>

      <!-- Contact buttons - fixed footer -->
      <div class="absolute bottom-0 left-0 right-0 bg-white rounded-b-[42px] z-10">
        <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 justify-center w-full p-4 sm:p-6 md:p-8 border-t border-gray-100">
          <!-- WhatsApp Button -->
          <button type="button" aria-label="Whatsapp" aria-labelledby="Whatsapp"
              @click="handleWhatsAppClick"
              class="w-full sm:flex-1 sm:max-w-[240px] bg-[#33CC33] font-bold hover:brightness-105 ease-linear transition-[filter] duration-150 text-xs sm:text-sm md:text-base whitespace-nowrap flex items-center justify-center gap-2 md:gap-3 rounded-[1.25rem] px-4 md:px-5 h-[40px] sm:h-[44px] md:h-[52px] text-white text-center">
              <img :src="whatsappIcon"
                  class="object-contain w-[19px] h-[18px] text-white" width="19"
                  height="18" alt />
              {{ $t('general.call_reseller_whatsapp') }}
          </button>
          
          <!-- Phone Button -->
          <button 
            @click="phoneNumber && phoneNumber2 ? showPhoneModal = true : handlePhoneClick(phoneNumber)" 
            class="w-full sm:flex-1 sm:max-w-[240px] bg-blue-500 font-bold hover:brightness-105 ease-linear transition-[filter] duration-150 text-xs sm:text-sm md:text-base whitespace-nowrap flex items-center justify-center gap-2 md:gap-3 rounded-[1.25rem] px-4 md:px-5 h-[40px] sm:h-[44px] md:h-[52px] text-white text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 11.637 11.637" class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
              <path id="call" d="M11.428,10.082a2.744,2.744,0,0,1-2.772,1.545,8.841,8.841,0,0,1-4-1.655A13.18,13.18,0,0,1,.515,5.034,4.144,4.144,0,0,1,.684.738,1.645,1.645,0,0,1,2,0c.8-.039.909.417,1.183,1.128.2.531.476,1.074.628,1.625C4.092,3.783,3.1,3.826,2.97,4.666c-.078.53.564,1.241.854,1.618A7.84,7.84,0,0,0,5.848,8.148c.442.279,1.154.78,1.66.5.779-.427.706-1.74,1.794-1.3a17.525,17.525,0,0,1,1.649.851c.832.448.794.912.476,1.875h0" transform="translate(0 0)" fill="#ffffff" fill-rule="evenodd"/>
            </svg>
            {{ $t('general.contact_phone') }}
          </button>
        </div>
      </div>
    </div>
  </Modal>
  
  <Modal
    v-if="showPhoneModal"
    :isOpen="showPhoneModal"
    :changeModalState="() => showPhoneModal = false"
    :modalTitle="$t('user.store.contact_store')"
    size="sm"
    rounded="24px"
  >
    <div class="relative bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
      <div class="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-primary/10 to-transparent rounded-t-3xl"></div>
      <div class="flex flex-col items-center gap-4 py-4">
        <p class="text-center text-gray-500">{{$t('user.store.contact_store_description')}}</p>
      </div>
      <div class="space-y-6 relative">
        <template v-if="phoneNumber">
          <div class="transform transition-all duration-200 hover:scale-[1.02]">
            <div class="flex items-center gap-4">
              <a
                :href="`tel:${phoneNumber}`"
                class="flex items-center flex-1 bg-primary text-white rounded-2xl px-6 py-4 font-semibold shadow-lg hover:shadow-primary/20 hover:bg-primary/90 transition-all duration-200"
                :aria-label="`Call ${phoneNumber}`"
                tabindex="0"
              >
                <span class="text-lg">{{ phoneNumber && phoneNumber.replace('+2', '') }}</span>
                <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.72 19.72 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13 1.05.37 2.07.72 3.06a2 2 0 0 1-.45 2.11l-.27.27a16 16 0 0 0 6.29 6.29l.27-.27a2 2 0 0 1 2.11-.45c.99.35 2.01.59 3.06.72A2 2 0 0 1 22 16.92z"/></svg>
              </a>
              <button
                @click="copyToClipboard(phoneNumber && phoneNumber.replace('+2', ''))"
                class="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors duration-200"
                :aria-label="`Copy ${phoneNumber}`"
              >
                <img :src="copyIcon" alt="Copy" class="w-5 h-5 opacity-70" />
              </button>
            </div>
          </div>
        </template>

        <template v-if="phoneNumber && phoneNumber2">
          <div class="w-full border-t border-gray-100"></div>
        </template>

        <template v-if="phoneNumber2">
          <div class="transform transition-all duration-200 hover:scale-[1.02]">
            <div class="flex items-center gap-4">
              <a
                :href="`tel:${phoneNumber2}`"
                class="flex items-center flex-1 bg-primary text-white rounded-2xl px-6 py-4 font-semibold shadow-lg hover:shadow-primary/20 hover:bg-primary/90 transition-all duration-200"
                :aria-label="`Call ${phoneNumber2}`"
                tabindex="0"
              >
                <span class="text-lg">{{ phoneNumber2 && phoneNumber2.replace('+2', '') }}</span>
                <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.72 19.72 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13 1.05.37 2.07.72 3.06a2 2 0 0 1-.45 2.11l-.27.27a16 16 0 0 0 6.29 6.29l.27-.27a2 2 0 0 1 2.11-.45c.99.35 2.01.59 3.06.72A2 2 0 0 1 22 16.92z"/></svg>
              </a>
              <button
                @click="copyToClipboard(phoneNumber2 && phoneNumber2.replace('+2', ''))" 
                class="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors duration-200"
                :aria-label="`Copy ${phoneNumber2}`"
              >
                <img :src="copyIcon" alt="Copy" class="w-5 h-5 opacity-70" />
              </button>
            </div>
          </div>
        </template>

        <div v-if="!phoneNumber && !phoneNumber2" class="flex items-center justify-center py-8">
          <div class="text-gray-400 text-center">
            <svg class="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 3h18v18H3V3z M15 9l-6 6m0-6l6 6"/></svg>
            <p>{{$t('user.store.no_phone_numbers') || 'No phone numbers available.'}}</p>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "@/components/generic/Modal.vue";
import copyIcon from "@/assets/icons/whatsapp_2.svg";
import { generateWhatsappLink } from "@/helpers";

export default {
  name: 'WarningSecurityContactModal',
  components: {
    Modal
  },
  data() {
    return {
      WhatsappButtonClass: "h-[52px]",
      showPhoneModal: false,
      storePhone1: null,
      storePhone2: null,
      copyIcon,
      whatsappIcon: copyIcon,
    }
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false
    },
    warningList: {
      type: Array,
      required: true,
      default: () => []
    },
    phoneNumber: {
      type: String,
      required: true
    },
    whatsAppNumber: {
      type: String,
      required: true
    },
    phoneNumber2: {
      type: String,
      required: false
    }
  },  
  methods: {
    closeModal() {
      this.$emit('close')
    },
    handlePhoneClick(phoneNumber) {
      window.location.href = `tel:${phoneNumber}`
    },
    copyToClipboard(number) {
      navigator.clipboard.writeText(number);
      this.notify({ type: 'info', msg: this.$t('general.link_was_copied') });
    },
    handleWhatsAppClick() {
      const whatsappLink = generateWhatsappLink({ 
        phone_number: this.whatsAppNumber, 
        message: this.$t('general.product_whatsapp_message'), 
        link: window?.location?.href || "" 
      });
      window.open(whatsappLink, '_blank');
    }
  },
  mounted(){
  }
}
</script>
